/*
 * The one message an event cannot carry whole.
 *
 * A message's rendered tree may be larger than a change_log row (64 KiB) while the message itself is
 * well inside every limit the composer enforces — a hundred-row table is the everyday example. The
 * server then sends the message without its tree and marks the event `renderOmitted`
 * (protocol.md §2.5.3, chat/hooks.go); this is the other half of that: the tap fetches the message
 * through the route that has no such ceiling and writes the tree into the loaded windows.
 *
 * Until it lands the row shows its markdown — `MessageBody` draws the source when there is no tree —
 * so nothing is missing at any moment, only unrendered. A fetch that fails leaves it that way, and
 * the next page of that conversation brings the tree with it like any other message's.
 *
 * A tree belongs to a *body* and not to a message, which is why `version` runs through everything
 * here: an edit while a fetch is in the air is a second body, it gets a request of its own, and the
 * answer that arrives for the body before it is dropped rather than drawn under the new markdown.
 *
 * It is a tap and not a reducer case because it does I/O: `applyEvent` is pure, and the store's
 * `onEvent` is where the platform layer hears about events it has to act on
 * (core/store/store.ts). The module's binding installs it (`bind.ts`).
 */

import type { Render } from "../../../core/render/nodes.ts"
import type { ConversationId, Model } from "../../../core/store/model.ts"
import type { ClientStore } from "../../../core/store/store.ts"
import { fetchMessageBySeq } from "../conversation/api.ts"
import type { MessageEdited } from "../events.ts"
import type { MentionRef } from "../wire/chat.gen.ts"
import type { MessageView } from "../wire/views.ts"
import { chatSlice, withChatSlice } from "./selectors.ts"
import type { ThreadWindow } from "./slice.ts"

/** The message one event left a tree out of, and the body the tree is wanted for. */
interface Missing {
	conv: ConversationId
	id: number
	seq: number
	/** The message's edit token: which body this fetch is about (protocol.md §2.6, `version`). */
	version: number
}

/**
 * Installs the tap and answers its unsubscribe, the way every `onEvent` caller does. The fetches in
 * flight live here, one entry per message with the newest body it is fetching for: two events about
 * the same body are one request, an event for a message nobody has loaded is no request at all, and
 * a second oversized edit while the first is still in the air is a request of its own. Dropping that
 * one — the message is "already in flight" — left the row showing the tree of the body before it,
 * for as long as the tab stayed open.
 */
export function installRenderRefetch(client: ClientStore): () => void {
	const inFlight = new Map<number, number>()
	return client.onEvent((event, result) => {
		if (result !== "applied") return
		const missing = missingRender(event)
		if (missing === undefined) return
		const fetching = inFlight.get(missing.id)
		if (fetching !== undefined && fetching >= missing.version) return
		if (!isLoaded(client.model, missing)) return
		inFlight.set(missing.id, missing.version)
		void fetchRender(client, missing).finally(() => {
			// Only this fetch's own entry: a newer one has taken the message over, and its round trip
			// is the one that must keep the door shut behind it.
			if (inFlight.get(missing.id) === missing.version) inFlight.delete(missing.id)
		})
	})
}

/** The two event types that carry a message body, when the tree stayed behind. */
function missingRender(event: { type: string; data?: unknown }): Missing | undefined {
	if (event.type !== "chat.message.new" && event.type !== "chat.message.edited") return undefined
	const data = event.data as (MessageView | MessageEdited) | undefined
	if (data === undefined || data.renderOmitted !== true) return undefined
	return { conv: data.conv, id: data.id, seq: data.seq, version: data.version }
}

/**
 * Whether the message is in a window this client is holding. A conversation nobody has opened is
 * not fetched for: its page will be read from the server whole, tree included, and a fetch now would
 * be one request per arriving message for a conversation nobody is looking at.
 */
function isLoaded(model: Model, m: Missing): boolean {
	if (model.messages[m.conv]?.rows.some((row) => row.id === m.id) === true) return true
	return Object.values(chatSlice(model).threads).some(
		(thread) => thread.root?.id === m.id || thread.rows.some((row) => row.id === m.id),
	)
}

async function fetchRender(client: ClientStore, m: Missing): Promise<void> {
	try {
		// The route answers the generated shape, whose tree is a list of open nodes; what the model
		// holds is the same bytes under the closed union the renderer switches on. Narrowing it here
		// is the boundary every read of this module crosses the same way (`api.get<HistoryPage>`).
		const { message } = (await fetchMessageBySeq(m.conv, m.seq)) as { message: MessageView }
		if (message.id !== m.id || message.render === undefined) return
		client.update((model) => withRender(model, m, message))
	} catch {
		// The row keeps its markdown, which is readable; the next page of the conversation carries
		// the tree. A retry loop here would be one per arriving event, against a server that has
		// just refused one.
	}
}

/**
 * Writes the fetched tree into every window that holds the message, and changes nothing else.
 *
 * A row the client has moved on from is left alone. The route answers the message as it stood when
 * it was read, and an edit that commits during the round trip reaches the socket first: writing this
 * tree over that body would draw the previous text under the new markdown until the tab is reloaded.
 * The edit's own fetch — a version of its own, so the tap does not swallow it — brings the tree that
 * belongs to it.
 */
function withRender(model: Model, m: Missing, fetched: MessageView): Model {
	const render: Render | undefined = fetched.render
	const mentions: MentionRef[] | undefined = fetched.mentions
	if (render === undefined) return model
	const rendered = (row: MessageView): MessageView => {
		if (row.version > fetched.version) return row
		const next: MessageView = { ...row, render }
		delete next.renderOmitted
		if (mentions !== undefined) next.mentions = mentions
		return next
	}
	let next = model
	const window = model.messages[m.conv]
	const at = window?.rows.findIndex((row) => row.id === m.id) ?? -1
	if (window !== undefined && at !== -1) {
		const row = window.rows[at] as MessageView
		const patched = rendered(row)
		if (patched !== row) {
			const rows = [...window.rows]
			rows[at] = patched
			next = { ...next, messages: { ...next.messages, [m.conv]: { ...window, rows } } }
		}
	}

	const slice = chatSlice(next)
	let threads = slice.threads
	for (const [key, thread] of Object.entries(threads)) {
		const patched = withRenderInThread(thread, m.id, rendered)
		if (patched === thread) continue
		if (threads === slice.threads) threads = { ...threads }
		threads[Number(key)] = patched
	}
	if (threads === slice.threads) return next
	return withChatSlice(next, { ...slice, threads })
}

/** One thread pane: the message may be its root, one of its replies, or in neither. */
function withRenderInThread(
	thread: ThreadWindow,
	id: number,
	rendered: (row: MessageView) => MessageView,
): ThreadWindow {
	let next = thread
	if (thread.root?.id === id) {
		const patched = rendered(thread.root)
		if (patched !== thread.root) next = { ...next, root: patched }
	}
	const at = thread.rows.findIndex((row) => row.id === id)
	if (at !== -1) {
		const row = thread.rows[at] as MessageView
		const patched = rendered(row)
		if (patched !== row) {
			const rows = [...thread.rows]
			rows[at] = patched
			next = { ...next, rows }
		}
	}
	return next
}
